import type {
  Referral,
  ReferralCreateRequest,
  ReferralListResponse,
  ReferralStatus,
} from "../../types/api.generated";

const getBaseUrl = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const getErrorMessage = async (response: Response) => {
  const fallbackMessage = "Referral registration failed. Please check the referral link and try again.";
  const text = await response.text();

  if (!text) return fallbackMessage;

  try {
    const body = JSON.parse(text) as { message?: unknown; error?: unknown };
    const message = body.message || body.error;

    if (typeof message === "string" && message.trim()) return message;
    if (Array.isArray(message)) {
      const firstMessage = message.find((item) => typeof item === "string" && item.trim());
      if (firstMessage) return firstMessage;
    }
  } catch {
    if (text.trim()) return text;
  }

  return fallbackMessage;
};

export const createReferral = async (payload: ReferralCreateRequest): Promise<Referral> => {
  const token = sessionStorage.getItem("tokenUser");
  const response = await fetch(`${getBaseUrl()}/referral`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response));
  }

  return response.json();
};

interface ReferralQueryParams {
  page?: number;
  limit?: number;
  status?: ReferralStatus;
}

const buildReferralUrl = ({ page, limit, status }: ReferralQueryParams) => {
  const base = getBaseUrl();
  const url = new URL(`${base}/referral`, window.location.origin);

  if (page) url.searchParams.set("page", String(page));
  if (limit) url.searchParams.set("limit", String(limit));
  if (status) url.searchParams.set("status", status);

  return base ? `${base}/referral${url.search}` : `/referral${url.search}`;
};

export const getReferrals = async (params: ReferralQueryParams = {}): Promise<ReferralListResponse> => {
  const token = sessionStorage.getItem("tokenUser");
  const response = await fetch(buildReferralUrl(params), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response));
  }

  return response.json();
};
